<template>
  <!-- the 32px content indent and the top margin exist to clear this shell's
       own left nav rail. horizon/midnight don't render that rail (its
       destinations live in the section nav), so there they are dead space and
       these pages sat further right than every other horizon page. same
       treatment alerts_shell already carries. -->
  <div class="bs-shell" :class="{ 'bs-shell--flush': horizonNavActive }">
    <div class="bs-layout">

      <!-- Collapseable left nav -->
      <div v-if="!horizonNavActive" class="bs-nav-wrapper" :class="{ 'bs-nav-collapsed': navCollapsed }">
        <div class="bs-nav-inner">

          <!-- Dashboard overview link -->
          <router-link
            :to="{ name: 'bookings_dashboard' }"
            class="bs-nav-home"
            :class="{ active: $route.name === 'bookings_dashboard' }"
          >
            <i class="fa fa-th-large bs-nav-home-icon"></i>
            Dashboard
          </router-link>

          <!-- Page links -->
          <router-link
            v-for="item in items"
            :key="item.route"
            :to="{ name: item.route, query: item.query }"
            class="bs-nav-sub-item"
            :class="{ active: $route.name === item.route }"
          >
            {{ item.name }}
          </router-link>

        </div>

        <button
          class="bs-nav-toggle"
          @click="toggleNav"
          :title="navCollapsed ? 'Expand menu' : 'Collapse menu'"
          :aria-label="navCollapsed ? 'Expand menu' : 'Collapse menu'"
        >
          <i class="fa" :class="navCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
        </button>
      </div>

      <!-- Page content -->
      <div class="bs-content">
        <router-view />
      </div>

    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getBookingPages } from '@/utils/booking_pages'
import horizonNavMixin from '@/nav/horizon_nav_mixin'

export default {
  name: 'booking_shell',
  mixins: [horizonNavMixin],
  data() {
    return {
      navCollapsed: false,
      navUserToggled: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant_type() {
      return this.user.types[this.user.tenant]
    },
    items() {
      return getBookingPages(this.tenant_type)
    },
  },
  mounted() {
    this._onResize = () => {
      // Keep the nav responsive to window width unless the user has explicitly toggled it
      if (this.navUserToggled) return
      this.navCollapsed = window.innerWidth < 1600
    }
    window.addEventListener('resize', this._onResize)
    this._onResize()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
  },
  methods: {
    toggleNav() {
      this.navCollapsed = !this.navCollapsed
      this.navUserToggled = true
    },
  },
}
</script>

<style scoped>
/* ── Outer layout ───────────────────────────────────────────────────── */
.bs-layout {
  display: flex;
  align-items: flex-start;
}

/* ── Nav wrapper (handles collapse transition) ──────────────────────── */
.bs-nav-wrapper {
  position: relative;
  display: flex;
  flex-shrink: 0;
}

.bs-nav-inner {
  width: 250px;
  min-width: 250px;
  overflow: hidden;
  transition: width 0.22s ease, min-width 0.22s ease;
  background-color: #e8eef5;
  border-right: 2px solid #dee2e6;
  padding: 12px 0;
}

.bs-nav-collapsed .bs-nav-inner {
  width: 0;
  min-width: 0;
  border-right: none;
  padding: 0;
}

/* ── Dashboard home link ────────────────────────────────────────────── */
.bs-nav-home {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #333 !important;
  text-decoration: none !important;
  border-bottom: 2px solid #c8d8e8;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  transition: background 0.12s;
}

.bs-nav-home:hover {
  background-color: #d0dfee;
  text-decoration: none !important;
}

.bs-nav-home.active {
  background-color: steelblue !important;
  color: white !important;
  border-bottom-color: #f0f0f0;
}

.bs-nav-home-icon {
  margin-right: 7px;
  font-size: 12px;
}

/* ── Page links ─────────────────────────────────────────────────────── */
.bs-nav-sub-item {
  display: block;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #333 !important;
  text-decoration: none !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-left: 3px solid transparent;
  position: relative;
  transition: background 0.1s, border-color 0.1s;
}

.bs-nav-sub-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 0;
  height: 1px;
  background-color: #c8d8e8;
}

.bs-nav-sub-item:hover {
  background-color: #dce8f5;
  text-decoration: none !important;
}

.bs-nav-sub-item.active {
  background-color: #dce8f5;
  border-left-color: steelblue;
  color: steelblue !important;
  font-weight: 600;
}

/* ── Collapse toggle nub ────────────────────────────────────────────── */
.bs-nav-toggle {
  position: absolute;
  top: 12px;
  right: -20px;
  width: 20px;
  height: 36px;
  background: #e8eef5;
  border: 1px solid #c8d8e8;
  border-left: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a6785;
  font-size: 11px;
  padding: 0;
  z-index: 10;
  transition: background 0.15s;
}

.bs-nav-toggle:hover {
  background: #d0dfee;
  color: steelblue;
}

.bs-nav-toggle:focus {
  outline: none;
}

/* ── Content area ───────────────────────────────────────────────────── */
.bs-shell {
  margin-top: 20px;
}

.bs-content {
  flex: 1;
  min-width: 0;
  padding-left: 32px;
}

/* no in-page nav rail to clear (see the template note): the pages inside
   supply their own gutters, so hand the space back to them */
.bs-shell--flush {
  margin-top: 0;
}

.bs-shell--flush .bs-content {
  padding-left: 0;
}
</style>
